import { useRef, useState } from "react";
import { parseNameplate, type FileInfo, type NameplateGuess } from "@handvero/shared";
import { api, errorText, upload } from "../api";
import { prepareUpload } from "./images";
import { Message, Sheet } from "./ui";

/*
 * Gerät erfassen: Typenschild fotografieren, Text direkt auf dem Gerät erkennen (Tesseract, selbst gehostet unter /ocr,
 * nichts verlässt das Handy), Angaben prüfen und als schriftliche Gerätekarte (PDF) im Ordner ablegen.
 * Die Erkennung ist nur ein Vorschlag. Was falsch gelesen wurde, ändert die Person mit einem Fingertipp.
 */

const OCR_EDGE = 2000;

interface Suggestion {
  key: string;
  label: string;
  title: string;
  date: string;
  everyMonths: number;
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** Vorschläge für Erinnerungen aus Garantie und Wartung der Gerätekarte. */
export function reminderSuggestions(name: string, warranty: string, serviceMonths: number, now = new Date()): Suggestion[] {
  const out: Suggestion[] = [];
  if (serviceMonths > 0) {
    const d = new Date(now);
    d.setUTCMonth(d.getUTCMonth() + serviceMonths);
    out.push({ key: "service", label: `Wartung von „${name}“ in ${serviceMonths} Monaten vormerken`, title: `Wartung: ${name}`, date: isoDay(d), everyMonths: serviceMonths });
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(warranty)) {
    const end = new Date(`${warranty}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() - 30);
    if (end > now) out.push({ key: "warranty", label: `Erinnerung 30 Tage vor Garantie-Ende von „${name}“`, title: `Garantie endet bald: ${name}`, date: isoDay(end), everyMonths: 0 });
  }
  return out;
}

/** Foto für die Erkennung vorbereiten: verkleinern, Graustufen, Kontrast strecken. */
async function prepareForOcr(file: File): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, OCR_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Kein Zeichenbereich");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const hist = new Uint32Array(256);
  for (let i = 0; i < d.length; i += 4) {
    const g = Math.round(0.299 * d[i]! + 0.587 * d[i + 1]! + 0.114 * d[i + 2]!);
    d[i] = d[i + 1] = d[i + 2] = g;
    hist[g]!++;
  }
  // Helligkeit auf den Bereich zwischen 2 % und 98 % strecken
  const total = canvas.width * canvas.height;
  let lo = 0;
  let hi = 255;
  for (let acc = 0; lo < 255 && (acc += hist[lo]!) < total * 0.02; lo++);
  for (let acc = 0; hi > 0 && (acc += hist[hi]!) < total * 0.02; hi--);
  const span = Math.max(1, hi - lo);
  for (let i = 0; i < d.length; i += 4) {
    const v = Math.max(0, Math.min(255, ((d[i]! - lo) * 255) / span));
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

export async function readNameplate(file: File, onProgress: (share: number) => void): Promise<string> {
  const canvas = await prepareForOcr(file);
  const { createWorker } = await import("tesseract.js");
  const worker = await createWorker("deu", 1, {
    workerPath: "/ocr/worker.min.js",
    corePath: "/ocr",
    langPath: "/ocr",
    workerBlobURL: false,
    logger: (m: { status: string; progress: number }) => {
      if (m.status === "recognizing text") onProgress(m.progress);
    },
  });
  try {
    const { data } = await worker.recognize(canvas);
    return data.text;
  } finally {
    await worker.terminate();
  }
}

type Fields = { name: string; manufacturer: string; model: string; serial: string; year: string; power: string; voltage: string; place: string; notes: string };
const EMPTY: Fields = { name: "", manufacturer: "", model: "", serial: "", year: "", power: "", voltage: "", place: "", notes: "" };

const LABELS: [keyof NameplateGuess, string, string][] = [
  ["manufacturer", "Hersteller", "zum Beispiel Viessmann"],
  ["model", "Typ / Modell", "steht oft hinter „Typ“ oder „Type“"],
  ["serial", "Seriennummer", "hinter „Ser.-Nr.“ oder „S/N“"],
  ["year", "Baujahr", "vierstellig, zum Beispiel 2019"],
  ["power", "Leistung", "zum Beispiel 19 kW"],
  ["voltage", "Spannung", "zum Beispiel 230 V"],
];

export function DeviceCaptureSheet({ folderId, folderName, onClose, onSaved }: { folderId: string; folderName: string; onClose: () => void; onSaved: (card: { fileId: string; name: string }) => void }) {
  const [step, setStep] = useState<"start" | "reading" | "form" | "saving" | "done">("start");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [raw, setRaw] = useState("");
  const [guessed, setGuessed] = useState(0);
  const [error, setError] = useState("");
  const [savedName, setSavedName] = useState("");
  const [warranty, setWarranty] = useState("");
  const [serviceMonths, setServiceMonths] = useState(0);
  const [remindersDone, setRemindersDone] = useState<string[]>([]);
  const camRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<HTMLInputElement>(null);

  async function onPhoto(list: FileList | null) {
    const file = list?.[0];
    if (!file) return;
    setError("");
    setPhoto(file);
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
    setStep("reading");
    setProgress(0);
    let text = "";
    try {
      text = await readNameplate(file, setProgress);
    } catch {
      // Ohne Erkennung geht es von Hand weiter, das Foto bleibt.
      setError("Das Foto konnte nicht automatisch gelesen werden. Sie können die Angaben unten selbst eintragen.");
    }
    const guess = parseNameplate(text);
    setRaw(text.trim());
    setGuessed(Object.values(guess).filter(Boolean).length);
    setFields({ ...EMPTY, ...guess, name: folderName });
    setStep("form");
  }

  const set = (k: keyof Fields) => (v: string) => setFields((f) => ({ ...f, [k]: v }));

  async function save() {
    if (!photo) return;
    setStep("saving");
    setError("");
    try {
      const prepared = await prepareUpload(photo, true);
      const form = new FormData();
      if (prepared.thumb) form.append("thumb", prepared.thumb, "vorschau.jpg");
      form.append("file", prepared.file, prepared.file.name);
      const saved = await upload<FileInfo>(`/api/folders/${folderId}/files`, form);
      const card = await api.post<{ fileId: string; name: string }>(`/api/folders/${folderId}/device-card`, { ...fields, warrantyUntil: warranty || undefined, serviceMonths: serviceMonths || undefined, photoFileId: saved.id });
      setSavedName(card.name);
      setStep("done");
      onSaved(card);
    } catch (err) {
      setError(errorText(err));
      setStep("form");
    }
  }

  const suggestions = reminderSuggestions(savedName || fields.name, warranty, serviceMonths);
  async function remind(sg: Suggestion) {
    try {
      await api.post("/api/home/reminders", { title: sg.title, firstDate: sg.date, everyMonths: sg.everyMonths });
      setRemindersDone((d) => [...d, sg.key]);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Sheet title="Gerät erfassen" onClose={onClose}>
      <div className="device">
        {step === "start" && (
          <>
            <p>
              <b>So gelingt das Foto:</b> Gehen Sie nah ans Typenschild, halten Sie das Handy gerade davor und vermeiden Sie Spiegelungen vom Licht. Alle Zeilen sollen scharf zu sehen sein.
            </p>
            <p className="muted-s">Die Schrift wird auf Ihrem Handy gelesen. Das Foto verlässt es erst beim Speichern und liegt dann in Ihrem Ordner „{folderName}“.</p>
            <button className="app-btn" onClick={() => camRef.current?.click()}>
              Typenschild fotografieren
            </button>
            <button className="app-btn ghost" onClick={() => pickRef.current?.click()}>
              Foto aus der Galerie wählen
            </button>
            <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { void onPhoto(e.target.files); e.target.value = ""; }} />
            <input ref={pickRef} type="file" accept="image/*" hidden onChange={(e) => { void onPhoto(e.target.files); e.target.value = ""; }} />
          </>
        )}
        {step === "reading" && (
          <>
            {preview && <img className="device-photo" src={preview} alt="Ihr Foto vom Typenschild" />}
            <p>
              <b>Vero liest das Typenschild …</b>
            </p>
            <progress max={1} value={progress || undefined} aria-label="Fortschritt beim Lesen" />
            <p className="muted-s">Beim ersten Mal lädt HandVero einmalig die Lesehilfe (ca. 6 MB). Das kann einen Moment dauern.</p>
          </>
        )}
        {(step === "form" || step === "saving") && (
          <>
            {preview && <img className="device-photo" src={preview} alt="Ihr Foto vom Typenschild" />}
            <Message kind={error && guessed === 0 ? "info" : "bad"}>{error}</Message>
            <p className="muted-s">
              {guessed > 0
                ? `Vero hat ${guessed} Angabe${guessed === 1 ? "" : "n"} erkannt. Bitte prüfen Sie jede Zeile gegen das Typenschild, Schrift wird manchmal falsch gelesen.`
                : "Es konnte nichts sicher erkannt werden. Tragen Sie ab, was Sie auf dem Typenschild sehen."}
            </p>
            <form
              className="app-form"
              onSubmit={(e) => {
                e.preventDefault();
                void save();
              }}
            >
              <label>
                Name des Geräts
                <input value={fields.name} onChange={(e) => set("name")(e.target.value)} required minLength={2} maxLength={120} placeholder="zum Beispiel Heizung Keller" />
              </label>
              {LABELS.map(([key, label, hint]) => (
                <label key={key}>
                  {label}
                  <input value={fields[key]} onChange={(e) => set(key)(e.target.value)} maxLength={160} placeholder={hint} />
                </label>
              ))}
              <label>
                Einbauort
                <input value={fields.place} onChange={(e) => set("place")(e.target.value)} maxLength={160} placeholder="zum Beispiel Heizraum, Keller" />
              </label>
              <label>
                Garantie bis (freiwillig)
                <input type="date" value={warranty} onChange={(e) => setWarranty(e.target.value)} />
              </label>
              <label>
                Wartung (freiwillig)
                <select value={serviceMonths} onChange={(e) => setServiceMonths(Number(e.target.value))}>
                  <option value={0}>Keine Angabe</option>
                  <option value={6}>Alle 6 Monate</option>
                  <option value={12}>Jedes Jahr</option>
                  <option value={24}>Alle 2 Jahre</option>
                </select>
              </label>
              <label>
                Bemerkung
                <textarea value={fields.notes} onChange={(e) => set("notes")(e.target.value)} maxLength={1500} rows={3} />
              </label>
              {raw && (
                <details className="device-raw">
                  <summary>Gelesener Text ansehen</summary>
                  <pre>{raw}</pre>
                </details>
              )}
              <button className="app-btn" disabled={step === "saving" || fields.name.trim().length < 2}>
                {step === "saving" ? "Wird gespeichert …" : "Als Gerätekarte speichern"}
              </button>
            </form>
          </>
        )}
        {step === "done" && (
          <>
            <Message kind="ok">Gespeichert: {savedName}</Message>
            <p>Die Gerätekarte und das Foto liegen jetzt im Ordner „{folderName}“.</p>
            {suggestions.map((sg) => (
              <button key={sg.key} className="app-btn ghost" disabled={remindersDone.includes(sg.key)} onClick={() => void remind(sg)}>
                {remindersDone.includes(sg.key) ? "Erinnerung angelegt" : sg.label}
              </button>
            ))}
            <button className="app-btn" onClick={onClose}>
              Fertig
            </button>
          </>
        )}
      </div>
    </Sheet>
  );
}
